'use client';

import { useEffect, useMemo, useState, useRef } from 'react';
import useSWR from 'swr';
import { apiFetch } from '../lib/api';
import { useTranslation } from '../lib/i18n';
import { getFormatLocale, useFormatLocale } from '../lib/useNationalEnvironment';
import Alert from './Alert';
import EmailInput from './EmailInput';
import PhoneInput from './PhoneInput';
import LoadingFallback from './LoadingFallback';
import styles from './BookingWidget.module.css';

type ReservationService = {
  id: number;
  publicSlug: string;
  ownerName: string | null;
  name: string;
  description: string | null;
  durationMinutes: number;
  location: string | null;
  maxDaysInAdvance: number;
  priceCzk: number | null;
};

type AvailableSlot = {
  startTime: string;
  endTime: string;
  available: boolean;
};

// Služba → CMS detail stránka (odhad ze slugu; jinak bez odkazu)
function servicePageUrl(svc: ReservationService): string | null {
  const s = svc.publicSlug.toLowerCase();
  if (s.includes('sportovn') || s.includes('vedsk')) return '/sportovnimasaz';
  if (s.includes('relaxan')) return '/relaxacnimasaz';
  if (s.includes('nsk') || s.includes('cinsk') || s.includes('tuin')) return '/cinskamasaz';
  if (s.includes('plosek') || s.includes('reflexn')) return '/reflexnimasazploseknohou';
  if (s.includes('spouovch') || s.includes('terapie')) return '/terapiespoustovychbodu';
  if (s.includes('raelin') || s.includes('zbal')) return '/raselinovezabaly';
  if (s.includes('thajsk')) return '/thajskamasaz';
  return null;
}

type Reservation = {
  id: number;
  publicToken: string;
  status: string;
  startTime: string;
  endTime: string;
};

function formatDateCs(date: Date): string {
  return date.toLocaleDateString(getFormatLocale(), { weekday: 'long', day: 'numeric', month: 'long' });
}

function formatDateShort(date: Date): string {
  return date.toLocaleDateString(getFormatLocale(), { weekday: 'short', day: 'numeric', month: 'numeric' });
}

function formatTimeCs(date: Date): string {
  return date.toLocaleTimeString(getFormatLocale(), { hour: '2-digit', minute: '2-digit' });
}

function formatPriceCzk(price: number | null | undefined): string | null {
  if (price == null) return null;
  return `${new Intl.NumberFormat(getFormatLocale()).format(price)} Kč`;
}

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * BookingWidget — embeddable reservation form for public reservation services.
 * Used by the CMS BOOKING block — same flow as /book/[slug].
 * Three visible steps: 1) service, 2) date+time (week navigation), 3) details.
 */
export default function BookingWidget({ slugs, compact = false }: { slugs: string[]; compact?: boolean }) {
  const { t } = useTranslation();
  useFormatLocale(); // ensures the national environment is loaded for getFormatLocale()

  // compact = nabídka se neopakuje (varianty jako chips ve kroku 2)
  const [step, setStep] = useState<1 | 2 | 3>(slugs.length === 1 || compact ? 2 : 1);
  const [activeSlug, setActiveSlug] = useState<string | null>(compact ? slugs[0] ?? null : (slugs.length === 1 ? slugs[0] : null));
  const [weekOffset, setWeekOffset] = useState(0); // týdny od aktuálního týdne
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<AvailableSlot | null>(null);
  const [slotsLeaving, setSlotsLeaving] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerNote, setCustomerNote] = useState('');
  const [remember, setRemember] = useState(true);
  const [honeypot, setHoneypot] = useState('');
  const startedAt = useRef(Date.now());
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [reservation, setReservation] = useState<Reservation | null>(null);

  // Zapamatované údaje zákazníka — localStorage, prefill při další návštěvě.
  const REMEMBER_KEY = 'armorik:booking:customer';
  useEffect(() => {
    try {
      const raw = localStorage.getItem(REMEMBER_KEY);
      if (raw) {
        const d = JSON.parse(raw) as { name?: string; email?: string; phone?: string };
        if (d.name) setCustomerName(d.name);
        if (d.email) setCustomerEmail(d.email);
        if (d.phone) setCustomerPhone(d.phone);
      }
    } catch { /* localStorage nedostupné */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load all offered services — jeden bulk request (N jednotlivých fetchů
  // by snadno minulo veřejný rate limit). Seřazeno dle pořadí slugů.
  const { data: services, error: servicesError, isLoading: servicesLoading } = useSWR<ReservationService[]>(
    slugs.length ? ['cms-booking-services', ...slugs] : null,
    async () => {
      const list = await apiFetch<ReservationService[]>(
        `/api/public/reservations/services?slugs=${slugs.map(encodeURIComponent).join(',')}`,
      );
      const order = new Map(slugs.map((s, i) => [s, i]));
      return list.slice().sort((a, b) => (order.get(a.publicSlug) ?? 999) - (order.get(b.publicSlug) ?? 999));
    },
  );

  const effectiveService = activeSlug && services ? services.find((s) => s.publicSlug === activeSlug) ?? null : null;

  // Předvybraná služba — ?service=<slug> (reload/přímý vstup) nebo
  // CustomEvent z tlačítka Rezervovat (bez reloadu).
  useEffect(() => {
    if (!services?.length || activeSlug) return;
    const want = new URLSearchParams(window.location.search).get('service');
    if (want && slugs.includes(want)) pickService(want);
  }, [services, activeSlug]);
  useEffect(() => {
    const h = (e: Event) => {
      const slug = (e as CustomEvent<string>).detail;
      if (slug && slugs.includes(slug)) pickService(slug);
    };
    window.addEventListener('cms-book-service', h);
    return () => window.removeEventListener('cms-book-service', h);
  }, [slugs]);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const maxDate = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + (effectiveService?.maxDaysInAdvance ?? 30));
    return d;
  }, [today, effectiveService]);

  const fromParam = useMemo(() => isoDate(today), [today]);
  const toParam = useMemo(() => isoDate(maxDate), [maxDate]);

  const { data: slots, error: slotsError, isLoading: slotsLoading } = useSWR<AvailableSlot[]>(
    effectiveService && activeSlug
      ? `/api/public/reservations/${encodeURIComponent(activeSlug)}/slots?from=${fromParam}&to=${toParam}`
      : null,
  );

  const slotsByDate = useMemo(() => {
    if (!slots) return new Map<string, AvailableSlot[]>();
    const map = new Map<string, AvailableSlot[]>();
    for (const slot of slots) {
      if (!slot.available) continue;
      const date = slot.startTime.split('T')[0];
      const list = map.get(date) ?? [];
      list.push(slot);
      map.set(date, list);
    }
    Array.from(map.values()).forEach((list) => {
      list.sort((a, b) => a.startTime.localeCompare(b.startTime));
    });
    return map;
  }, [slots]);

  // Viditelný týden: po–ne od pondělí (today + weekOffset*7), oříznutý na today..maxDate.
  const weekDays = useMemo(() => {
    // Rolovací 7denní okno od dneška — minulé dny se nezobrazují.
    const start = new Date(today);
    start.setDate(start.getDate() + weekOffset * 28);
    const days: { iso: string; date: Date; hasSlots: boolean; inRange: boolean }[] = [];
    for (let i = 0; i < 28; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const iso = isoDate(d);
      const inRange = d >= today && d <= maxDate;
      days.push({ iso, date: d, hasSlots: slotsByDate.has(iso), inRange });
    }
    return days;
  }, [today, weekOffset, maxDate, slotsByDate]);

  const maxWeekOffset = useMemo(() => {
    return Math.max(0, Math.floor((maxDate.getTime() - today.getTime()) / (28 * 86400000)));
  }, [today, maxDate]);

  const pickService = (slug: string) => {
    setActiveSlug(slug);
    setSelectedDate(null);
    setSelectedSlot(null);
    setWeekOffset(0);
    setStep(2);
  };

  const removeService = () => {
    setActiveSlug(null);
    setSelectedDate(null);
    setSelectedSlot(null);
    setWeekOffset(0);
    setStep(1);
  };

  const goBack = () => {
    if (step === 3) {
      setStep(2);
    } else if (step === 2 && services && services.length > 0) {
      removeService();
    }
  };

  const handleBook = async () => {
    if (!activeSlug || !selectedSlot || !customerName.trim() || !customerEmail.trim() || !customerPhone.trim()) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const created = await apiFetch<Reservation>(`/api/public/reservations/${encodeURIComponent(activeSlug)}/book`, {
        method: 'POST',
        body: JSON.stringify({
          startTime: selectedSlot.startTime,
          customerName: customerName.trim() || null,
          customerEmail: customerEmail.trim(),
          customerPhone: customerPhone.trim() || null,
          customerNote: customerNote.trim() || null,
          website: honeypot,
          startedAt: startedAt.current,
        }),
      });
      setReservation(created);
      try {
        if (remember) {
          localStorage.setItem(REMEMBER_KEY, JSON.stringify({ name: customerName.trim(), email: customerEmail.trim(), phone: customerPhone.trim() }));
        } else {
          localStorage.removeItem(REMEMBER_KEY);
        }
      } catch { /* localStorage nedostupné — soukromý režim */ }
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t('chybaPřiVytvářeníRezervace'));
    } finally {
      setSubmitting(false);
    }
  };

  if (servicesLoading) {
    return <div className="flex justify-center py-8"><LoadingFallback /></div>;
  }

  // Rozlišit "nelze načíst" (síť/rate-limit) od "služba neexistuje" —
  // jinak se za dočasný 429 vydává zavádějící "stránka nenalezena".
  if (servicesError) {
    const msg = servicesError instanceof Error ? servicesError.message : '';
    return <Alert type="error">{msg || t('error')} {t('bookingTryAgain')}</Alert>;
  }

  if (!services?.length) {
    return <Alert type="error">{t('bookPageNotFound')}. {t('bookPageNotFoundDesc')}</Alert>;
  }

  if (reservation && effectiveService) {
    return (
      <div className="bg-cream border border-wheat rounded-lg p-6 text-center">
        <svg className={styles.checkWrap} viewBox="0 0 52 52" aria-hidden="true">
          <circle className={styles.checkCircle} cx="26" cy="26" r="24" />
          <path className={styles.checkPath} d="M15 27l8 8 15-16" />
        </svg>
        <h3 className="text-xl font-bold text-bark mb-3">{t('bookingWidgetSent')}</h3>
        <p className="text-wood mb-2">{t('bookingWidgetThanks')}</p>
        <p className="text-wood mb-4">
          <strong>{effectiveService.name}</strong> — {formatDateCs(new Date(reservation.startTime))}{' '}
          {formatTimeCs(new Date(reservation.startTime))}
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <a href={`/api/public/reservations/bookings/${reservation.publicToken}/ics`} download
            className="inline-block bg-moss text-cream py-2 px-4 rounded hover:bg-moss/90 text-sm font-medium">
            📅 {t('bookingAddToCalendar')}
          </a>
          <a href={`/bookings/${reservation.publicToken}`} className="inline-block text-moss hover:underline text-sm">
            {t('bookingWidgetView')} →
          </a>
        </div>
      </div>
    );
  }

  const stepLabels = [t('bookingStepService'), t('bookingStepTime'), t('bookingStepDetails')];

  // Žádná nabídka služeb — výběr se dělá výhradně tlačítky Rezervovat
  // na stránce (cms-book-service event). Prázdný stav = elegantní výzva.
  if (step === 1 || !activeSlug) {
    return (
      <div className="py-10 text-center">
        <p className="text-wood/60 italic text-lg" style={{ fontFamily: 'Georgia, serif' }}>
          {t('bookingWaiting')}…
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Progress — 3 kroky */}
      <div className="flex items-center gap-2 text-sm">
        {stepLabels.map((label, i) => {
          const n = i + 1;
          const active = step === n;
          const done = step > n;
          return (
            <div key={n} className="flex items-center gap-2">
              {i > 0 && <span className="text-wheat">›</span>}
              <span className={`inline-flex items-center gap-1.5 ${active ? 'text-bark font-semibold' : done ? 'text-moss' : 'text-wood/60'}`}>
                <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-xs ${
                  active ? 'bg-moss text-cream' : done ? 'bg-moss/30 text-moss-dark' : 'bg-wheat/40 text-wood'
                }`}>{n}</span>
                {label}
              </span>
            </div>
          );
        })}
        {step > 1 && !(compact && step === 2) && (
          <button type="button" onClick={goBack}
            className="ml-auto text-sm text-moss hover:underline">
            {t('bookingBack')}
          </button>
        )}
      </div>

      {/* Krok 2: výběr termínu */}
      {step === 2 && effectiveService && (
        <div className={`space-y-4 ${styles.stepIn}`}>
          <div className="bg-cream border border-wheat rounded-lg p-5">
            <div className="flex items-start justify-between gap-2">
              <h3 className="text-lg font-semibold text-bark">
                {servicePageUrl(effectiveService)
                  ? <a href={servicePageUrl(effectiveService)!} className="text-moss hover:underline">{effectiveService.name}</a>
                  : effectiveService.name}
              </h3>
              <button type="button" onClick={removeService}
                title={t('bookingRemoveService')}
                className="shrink-0 text-wood/50 hover:text-rust-dark text-lg leading-none px-1">
                ✕
              </button>
            </div>
            {services.length === 1 && effectiveService.description && (
              <p className="text-wood mt-1 text-sm whitespace-pre-wrap">{effectiveService.description}</p>
            )}
            <div className="mt-2 text-sm text-wood space-x-4">
              <span>{t('bookingWidgetDuration')}: {effectiveService.durationMinutes} min</span>
              {formatPriceCzk(effectiveService.priceCzk) && (
                <span>{t('bookingWidgetPrice')}: <strong>{formatPriceCzk(effectiveService.priceCzk)}</strong></span>
              )}
              {effectiveService.location && <span>{t('bookingsMisto')} {effectiveService.location}</span>}
            </div>
          </div>

          <div className="bg-cream border border-wheat rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-bark">{t('bookingWidgetChooseDate')}</h4>
              <div className="flex gap-1">
                <button type="button" onClick={() => setWeekOffset((w) => Math.max(0, w - 1))}
                  disabled={weekOffset === 0}
                  className="px-2 py-1 rounded border border-wheat text-wood hover:bg-wheat/40 disabled:opacity-40 text-sm"
                  aria-label={t('bookingPrevWeek')}>‹</button>
                <button type="button" onClick={() => setWeekOffset((w) => Math.min(maxWeekOffset, w + 1))}
                  disabled={weekOffset >= maxWeekOffset}
                  className="px-2 py-1 rounded border border-wheat text-wood hover:bg-wheat/40 disabled:opacity-40 text-sm"
                  aria-label={t('bookingNextWeek')}>›</button>
              </div>
            </div>

            {slotsLoading ? (
              <p className="text-wood text-sm">{t('bookingWidgetLoading')}</p>
            ) : slotsError ? (
              <p className="text-rust-dark bg-rust/20 border border-rust/30 rounded p-3 text-sm">{t('bookingWidgetSlotsError')}</p>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-7 gap-1">
                  {weekDays.map((d) => {
                    const enabled = d.inRange && d.hasSlots;
                    return (
                      <button
                        key={d.iso}
                        type="button"
                        disabled={!enabled}
                        onClick={() => { if (d.iso === selectedDate) return; setSlotsLeaving(true); setSelectedSlot(null); setTimeout(() => { setSelectedDate(d.iso); setSlotsLeaving(false); }, 220); }}
                        style={{ animationDelay: `${weekDays.indexOf(d) * 35}ms` }}
                        className={`px-1 py-2 rounded border text-xs sm:text-sm text-center font-medium ${styles.dayChip} ${
                          selectedDate === d.iso
                            ? 'bg-moss text-cream border-moss'
                            : enabled
                              ? 'bg-linen border-wheat text-bark hover:bg-wheat/50'
                              : 'bg-linen/40 border-wheat/50 text-wood/40 cursor-not-allowed'
                        }`}
                      >
                        {formatDateShort(d.date)}
                      </button>
                    );
                  })}
                </div>

                {selectedDate && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                    {slotsByDate.get(selectedDate)?.map((slot, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => { setSelectedSlot(slot); setStep(3); }}
                        style={{ animationDelay: `${idx * (slotsLeaving ? 28 : 75)}ms` }}
                        className={`px-2 py-2 rounded border text-sm text-center bg-linen border-wheat text-bark hover:bg-wheat/50 ${slotsLeaving ? styles.slotOut : styles.slotChip}`}
                      >
                        {formatTimeCs(new Date(slot.startTime))}
                      </button>
                    ))}
                  </div>
                )}

                {!selectedDate && !slotsLoading && (
                  <p className="text-wood/70 text-sm">{t('bookingSelectDayHint')}</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Krok 3: údaje */}
      {step === 3 && selectedSlot && effectiveService && (
        <div className={`bg-cream border border-wheat rounded-lg p-5 ${styles.stepIn}`}>
          <h4 className="font-semibold text-bark mb-3">{t('bookingWidgetYourDetails')}</h4>
          <p className="text-wood text-sm mb-3">
            {t('bookingWidgetSelected')}{' '}
            <strong>
              {effectiveService.name} — {formatDateCs(new Date(selectedSlot.startTime))} {formatTimeCs(new Date(selectedSlot.startTime))} – {formatTimeCs(new Date(selectedSlot.endTime))}
            </strong>
            {formatPriceCzk(effectiveService.priceCzk) && <span> · {formatPriceCzk(effectiveService.priceCzk)}</span>}
          </p>

          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-bark mb-1">{t('bookingName')} <span className="text-rust-dark">*</span></label>
              <input type="text" value={customerName} onChange={(e) => setCustomerName(e.target.value)} autoComplete="name"
                className="w-full p-2 border border-wheat rounded bg-linen/50 text-bark focus:ring-1 focus:ring-moss outline-none"
                placeholder={t('bookingNamePlaceholder')} />
            </div>
            <div>
              <label className="block text-sm font-medium text-bark mb-1">{t('bookingEmail')} <span className="text-rust-dark">*</span></label>
              <EmailInput value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} autoComplete="email"
                className="w-full p-2 border border-wheat rounded bg-linen/50 text-bark focus:ring-1 focus:ring-moss outline-none"
                placeholder="jan@example.cz" required showLabel={false} />
            </div>
            <div>
              <label className="block text-sm font-medium text-bark mb-1">{t('bookingPhone')} <span className="text-rust-dark">*</span></label>
              <PhoneInput value={customerPhone} onChange={setCustomerPhone} showLabel={false} autoComplete="tel"
                className="w-full p-2 border border-wheat rounded bg-linen/50 text-bark focus:ring-1 focus:ring-moss outline-none"
                placeholder={t('bookingPhonePlaceholder')} />
            </div>
            <div>
              <label className="block text-sm font-medium text-bark mb-1">{t('bookingNote')}</label>
              <textarea value={customerNote} onChange={(e) => setCustomerNote(e.target.value)} rows={3}
                className="w-full p-2 border border-wheat rounded bg-linen/50 text-bark focus:ring-1 focus:ring-moss outline-none"
                placeholder={t('bookingNotePlaceholder')} />
            </div>

            {submitError && (
              <div className="bg-rust/20 text-rust-dark border border-rust/30 rounded p-3 text-sm">{submitError}</div>
            )}

            <input
              type="text" name="website" value={honeypot} onChange={(e) => setHoneypot(e.target.value)}
              tabIndex={-1} autoComplete="off" aria-hidden="true"
              style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0 }}
            />
            <label className="flex items-center gap-2 text-sm text-wood cursor-pointer select-none">
              <input type="checkbox" checked={remember} onChange={(e) => { setRemember(e.target.checked); if (!e.target.checked) { try { localStorage.removeItem(REMEMBER_KEY); } catch { /* noop */ } } }} className="accent-moss" />
              {t('bookingRememberDetails')}
            </label>

            <div className="pt-2">
              <button type="button" onClick={handleBook} disabled={submitting || !customerName.trim() || !customerEmail.trim() || !customerPhone.trim()}
                className="w-full bg-moss text-cream py-2 px-4 rounded hover:bg-moss/90 disabled:opacity-50 disabled:cursor-not-allowed font-medium">
                {submitting ? t('caseSending') : t('odeslatŽádostORezervaci')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
